import React, { useCallback, useState } from 'react';
import tw from 'tailwind-styled-components';
import PropTypes from "prop-types";
import { Alert, Button, ButtonGroup, ButtonGroupItem, Dropdown } from '@boozallen-uip/uswds-react-core';
import useAccount from '../../hooks/useAccount.hook';
import { formatShortDisplayDate } from '../../utlities/common.funcs';
import denialReasonOptions from './denialReasonOptions';

const UserRequestContainer = tw.div`grid grid-cols-12 border-2 border-gray-lighter mb-4 rounded-md p-4`;

export default function PendingUserRequest({ request, onChange, accessLevelOptions }) {
  const [accessLevel, setAccessLevel] = useState(null);
  const [denialReason, setDenialReason] = useState(null);
  const [denialReasonDropdownKey, setDenialReasonDropdownKey] = useState(0);
  const [hasApproveError, setHasApproveError] = useState(false);
  const { approveUserAccountRequest, denyUserAccountRequest } = useAccount();
  const accessLevelHelpId = `access-level-help-${request.id}`;

  const approveHandler = useCallback(async (userRequest, selectedLevel) => {
    setHasApproveError(false);
    try {
      await approveUserAccountRequest({requestId: userRequest.id, accessLevel : selectedLevel});
      onChange();
    } catch {
      setHasApproveError(true);
    }
  }, []);

  const denyHandler = useCallback(async (userRequest, selectedDenialReason) => {
    if (!selectedDenialReason) {
      return;
    }
    await denyUserAccountRequest({
      requestId: userRequest.id,
      denialReason: selectedDenialReason
    });
    onChange();
  }, []);

  return (
  <UserRequestContainer>
    <div className='col-start-1 col-span-8'>
      <p><span className="font-bold">Name:</span> {request.name}</p>
      <p><span className="font-bold">Email:</span> {request.userName}</p>
      <p><span className="font-bold">Organization:</span> {request.organizationName}</p>
      <p><span className="font-bold">Requested on:</span> {formatShortDisplayDate(request.createdDate)}</p>
      <p><span className="font-bold">Additional comments:</span> {request.additionalComments}</p>
    </div>
    <div className="col-span-4 [&>label]:m-0">
      <Dropdown
        id={`access-level-${request.id}`}
        optionRenderer={(o) => o.label}
        options={accessLevelOptions}
        comparator={(option, selected) => option?.value === selected}
        value={accessLevel}
        onChange={(e) => {
          setAccessLevel(e.detail.value.value);
          setHasApproveError(false);
        }}
        aria-label="Access level"
        aria-describedby={accessLevelHelpId}
        className='mb-4'
      >
        Access level:
      </Dropdown>
      <Dropdown
        key={`denial-reason-${request.id}-${denialReasonDropdownKey}`}
        id={`denial-reason-${request.id}`}
        optionRenderer={(o) => o.label}
        options={denialReasonOptions}
        comparator={(option, selected) => option?.value === selected}
        value={denialReason}
        onChange={(e) => setDenialReason(e.detail.value.value)}
        className='mb-4'
      >
        Denial reason:
      </Dropdown>
      <p id={accessLevelHelpId} className="mb-3 text-sm text-base-dark">
        Select an access level to enable the Approve button.
      </p>
      <p className="mb-3 text-sm text-base-dark">
        Select a denial reason to enable the Deny button.
      </p>
      {denialReason ? (
        <p className="mb-3 text-sm text-base-dark">
          {denialReasonOptions.find((option) => option.value === denialReason)?.description}
        </p>
      ) : null}
      {denialReason ? (
        <Button
          unstyled
          type="button"
          className="mb-4 text-accent-cool underline"
          onClick={() => {
            setDenialReason(null);
            setDenialReasonDropdownKey((prev) => prev + 1);
          }}
        >
          Clear reason
        </Button>
      ) : null}
      {hasApproveError ? (
        <Alert type="error" slim className="mb-3">
          Account not approved. Please try again in a few moments.
        </Alert>
      ) : null}
      <ButtonGroup className='flex'>
        <ButtonGroupItem className='flex-1'>
          <Button className='w-full' outline onClick={() => denyHandler(request, denialReason)} disabled={!denialReason}>Deny</Button>
        </ButtonGroupItem>
        <ButtonGroupItem className='flex-1'>
          <Button className='w-full' onClick={() => approveHandler(request, accessLevel)} disabled={!accessLevel}>Approve</Button>
        </ButtonGroupItem>
      </ButtonGroup>
    </div>
  </UserRequestContainer>
  )
}

PendingUserRequest.propTypes = {
  request: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  accessLevelOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
  ).isRequired,
};
